const BASE_URL = 'https://homesroofing.com';
const DEFAULT_IMAGE = `${BASE_URL}/img/HomesRoofingLogo.jpg`;
const DEFAULT_TITLE = 'Homes Roofing Co | Roofing Contractor in Michigan';
const DEFAULT_DESCRIPTION = 'Homes Roofing Co is a licensed and insured roofing contractor based in Niles, Michigan, offering roof repair, roof replacement, fascia, soffit, gutter, and siding services.';
const DEFAULT_KEYWORDS = 'Michigan roofing contractor, roof repair Michigan, roof replacement Michigan, Gaylord roof repair, Niles roofing';
const DEFAULT_ROBOTS = 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1';

function ensureMetaTag(attribute, value) {
	const selector = `meta[${attribute}="${value}"]`;
	let tag = document.querySelector(selector);

	if (!tag) {
		tag = document.createElement('meta');
		tag.setAttribute(attribute, value);
		document.head.appendChild(tag);
	}

	return tag;
}

function ensureLinkTag(rel) {
	let tag = document.querySelector(`link[rel="${rel}"]`);

	if (!tag) {
		tag = document.createElement('link');
		tag.setAttribute('rel', rel);
		document.head.appendChild(tag);
	}

	return tag;
}

function toAbsoluteUrl(path = '/') {
	return new URL(path, BASE_URL).toString();
}

function setMetaName(name, content) {
	ensureMetaTag('name', name).setAttribute('content', content);
}

function setMetaProperty(property, content) {
	ensureMetaTag('property', property).setAttribute('content', content);
}

export function upsertJsonLd(id, payload) {
	let script = document.querySelector(`script[data-seo-schema="${id}"]`);

	if (!script) {
		script = document.createElement('script');
		script.type = 'application/ld+json';
		script.setAttribute('data-seo-schema', id);
		document.head.appendChild(script);
	}

	script.textContent = JSON.stringify(payload);
}

export function removeJsonLd(id) {
	const script = document.querySelector(`script[data-seo-schema="${id}"]`);

	if (script) {
		script.remove();
	}
}

function buildBreadcrumbSchema(route) {
	const itemListElement = route.matched
		.filter((record) => record.name && record.name !== 'NotFound')
		.map((record, index) => ({
			'@type': 'ListItem',
			position: index + 1,
			name: record.meta?.breadcrumb || record.meta?.shortTitle || record.name,
			item: toAbsoluteUrl(record.path === '' ? '/' : record.path)
		}));

	if (!itemListElement.length) {
		return null;
	}

	return {
		'@context': 'https://schema.org',
		'@type': 'BreadcrumbList',
		itemListElement
	};
}

function buildWebPageSchema(route, seo) {
	return {
		'@context': 'https://schema.org',
		'@type': seo.schemaType || 'WebPage',
		name: seo.shortTitle || seo.title,
		description: seo.description,
		url: toAbsoluteUrl(seo.canonicalPath || route.path || '/'),
		isPartOf: {
			'@type': 'WebSite',
			name: 'Homes Roofing Co',
			url: BASE_URL
		},
		primaryImageOfPage: DEFAULT_IMAGE
	};
}

export function applyRouteSeo(route) {
	const seo = route.meta?.seo || {};
	const title = seo.title || DEFAULT_TITLE;
	const description = seo.description || DEFAULT_DESCRIPTION;
	const keywords = seo.keywords || DEFAULT_KEYWORDS;
	const robots = seo.robots || DEFAULT_ROBOTS;
	const canonicalUrl = toAbsoluteUrl(seo.canonicalPath || route.path || '/');
	const image = seo.image || DEFAULT_IMAGE;
	const type = seo.type || 'website';

	document.title = title;

	setMetaName('description', description);
	setMetaName('keywords', keywords);
	setMetaName('robots', robots);
	setMetaName('twitter:card', 'summary_large_image');
	setMetaName('twitter:title', title);
	setMetaName('twitter:description', description);
	setMetaName('twitter:image', image);
	setMetaName('geo.region', 'US-MI');
	setMetaName('geo.placename', 'Michigan');

	setMetaProperty('og:site_name', 'Homes Roofing Co');
	setMetaProperty('og:locale', 'en_US');
	setMetaProperty('og:title', title);
	setMetaProperty('og:description', description);
	setMetaProperty('og:type', type);
	setMetaProperty('og:url', canonicalUrl);
	setMetaProperty('og:image', image);

	ensureLinkTag('canonical').setAttribute('href', canonicalUrl);

	const breadcrumbSchema = buildBreadcrumbSchema(route);
	const webPageSchema = buildWebPageSchema(route, seo);

	upsertJsonLd('route-webpage', webPageSchema);

	if (breadcrumbSchema) {
		upsertJsonLd('route-breadcrumbs', breadcrumbSchema);
	} else {
		removeJsonLd('route-breadcrumbs');
	}
}

export { BASE_URL, DEFAULT_IMAGE, DEFAULT_ROBOTS };
